import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MessageKey, messages } from './messages';

export type Lang = 'vi' | 'en';

export const LANGUAGES: { code: Lang; label: string; short: string }[] = [
  { code: 'vi', label: 'Tiếng Việt', short: 'VI' },
  { code: 'en', label: 'English', short: 'EN' }
];

const STORAGE_KEY = 'todoapp.lang';

type Params = Record<string, string | number>;

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: MessageKey, params?: Params) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStored(): Lang | null {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === 'vi' || saved === 'en' ? saved : null;
  } catch {
    return null;
  }
}

/** Thay {tên} bằng giá trị. Đủ cho mọi câu trong ứng dụng này, không cần thư viện. */
function interpolate(template: string, params?: Params): string {
  if (!params) return template;

  return Object.keys(params).reduce(
    (text, name) => text.split('{' + name + '}').join(String(params[name])),
    template
  );
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  // Mặc định là tiếng Việt chứ không đoán theo ngôn ngữ trình duyệt: người dùng của ứng dụng này
  // là người Việt, và quan trọng hơn — thông báo lỗi do máy chủ trả về hiện chỉ có tiếng Việt, nên
  // tự nhảy sang tiếng Anh sẽ tạo ra một màn hình nửa nọ nửa kia.
  const [lang, setLangState] = useState<Lang>(() => readStored() ?? 'vi');

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);

    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* không lưu được thì lựa chọn chỉ sống trong phiên này */
    }
  }, []);

  const t = useCallback(
    (key: MessageKey, params?: Params) => interpolate(messages[key][lang], params),
    [lang]
  );

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n phải được dùng bên trong I18nProvider.');
  }

  return context;
}
